import sewerIcon from '@/assets/img/sewerIcon.png';
import waterIcon from '@/assets/img/waterIcon.png';
import { locationEventsService } from '@/events/location.events';
import { ComboboxLegacy as Combobox } from '@hydrodigital/combobox';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import LocationService from '../../domain/Location/LocationService';
import useLocalStorage from '../../hooks/useLocalStorage';
const cdnBase = import.meta.env.VITE_AZURE_CDN;
const locationService = new LocationService();

export default function ComboboxLocations({ aditionalStyle, handleClickSelect, size = 'middle' }) {
	const [locations, setLocations] = useState([]);
	const [dataSelect, setDataSelect] = useState<string | null>(null);
	const [dataPicker, setDataPicker] = useState([]);

	const location = useLocation();
	const navigate = useNavigate();
	const [selectedLocationInfo, setSelectedLocationInfo] = useLocalStorage(
		'selectedLocationInfo',
		null,
	);

	function renderNetworkIcon(networkType) {
		if (networkType === 'SEWER') {
			return <img style={{ width: '18px', height: '18px' }} src={sewerIcon} alt="sewerIcon" />;
		}
		if (networkType === 'WATER') {
			return <img style={{ width: '18px', height: '18px' }} src={waterIcon} alt="waterIcon" />;
		}
		return null;
	}

	function truncateLocationName(name) {
		const s = String(name || '');
		return s.length > 20 ? `${s.slice(0, 20)}...` : s;
	}

	// Build nested options with children using previous icons
	function mapLocationsToNestedOptions(nodes) {
		return nodes.map((location) => {
			const iconNode = renderNetworkIcon(location.networkType);

			// Truncate location name to 20 characters with ellipsis
			const label = truncateLocationName(location.locationName);

			const option = {
				value: String(location.locationId),
				label,
				icon: iconNode,
				...(location.canShowLocation ? {} : { disabled: true }),
			};

			if (
				location.hasChild &&
				Array.isArray(location.childLocations) &&
				location.childLocations.length > 0
			) {
				(option as any).children = mapLocationsToNestedOptions(location.childLocations);
			}

			return option;
		});
	}

	function handleGetLocations() {
		const userId = localStorage.getItem('userId') || '';
		const resLocationByUser = locationService.getLocationsByUser(userId);
		resLocationByUser.then((value) => {
			setLocations(value as any);
		});
	}

	function findDefaultSelected(locations) {
		for (const loc of locations) {
			if (loc.defaultSelected === true) {
				return loc;
			}
			if (Array.isArray(loc.childLocations) && loc.childLocations.length > 0) {
				const found = findDefaultSelected(loc.childLocations);
				if (found) {
					return found;
				}
			}
		}
		return null;
	}

	function findLocationById(locations, locationId) {
		for (const loc of locations) {
			if (loc.locationId === locationId) {
				return loc;
			}
			if (Array.isArray(loc.childLocations) && loc.childLocations.length > 0) {
				const found = findLocationById(loc.childLocations, locationId);
				if (found) {
					return found;
				}
			}
		}
		return null;
	}

	useEffect(() => {
		handleGetLocations();
	}, []);

	useEffect(() => {
		if (selectedLocationInfo && (selectedLocationInfo as any).locationId) {
			setDataSelect((selectedLocationInfo as any).locationId);
		}
	}, []);

	useEffect(() => {
		if (locations.length > 0) {
			const nestedOptions = mapLocationsToNestedOptions(locations);
			setDataPicker(nestedOptions);

			if (selectedLocationInfo && (selectedLocationInfo as any).locationId) {
				const savedLocation = findLocationById(locations, (selectedLocationInfo as any).locationId);
				if (savedLocation && savedLocation.canShowLocation) {
					setDataSelect(String((selectedLocationInfo as any).locationId) as any);
					if (handleClickSelect) {
						handleClickSelect((selectedLocationInfo as any).locationId);
					}
					return;
				}
			}

			const defaultLocation = findDefaultSelected(locations);
			if (defaultLocation) {
				setDataSelect(String(defaultLocation.locationId) as any);

				const locationInfo = {
					locationId: defaultLocation.locationId,
					locationName: defaultLocation.locationName,
					displayLocationLabel: truncateLocationName(defaultLocation.locationName),
					networkType: defaultLocation.networkType,
					canShowLocation: defaultLocation.canShowLocation,
					defaultSelected: defaultLocation.defaultSelected,
					hasChild: defaultLocation.hasChild,
					parentLocationId: defaultLocation.parentLocationId || null,
					...defaultLocation,
					selectedAt: new Date().toISOString(),
				};
				setSelectedLocationInfo(locationInfo);

				if (handleClickSelect) {
					handleClickSelect(defaultLocation.locationId);
				}
			}
		}
	}, [locations, selectedLocationInfo]);

	const handleChange = (value) => {
		setDataSelect(String(value) as any);

		const isDiagramBlockRoute = /^\/diagram-block\/\d+\/\d+$/.test(location.pathname);

		if (value && locations.length > 0) {
			const selectedLocation = findLocationById(locations, Number(value));
			if (selectedLocation) {
				const locationInfo = {
					locationId: selectedLocation.locationId,
					locationName: selectedLocation.locationName,
					displayLocationLabel: truncateLocationName(selectedLocation.locationName),
					networkType: selectedLocation.networkType,
					canShowLocation: selectedLocation.canShowLocation,
					defaultSelected: selectedLocation.defaultSelected,
					hasChild: selectedLocation.hasChild,
					parentLocationId: selectedLocation.parentLocationId || null,

					...selectedLocation,
					selectedAt: new Date().toISOString(),
				};
				setSelectedLocationInfo(locationInfo);
			}
		} else {
			setSelectedLocationInfo(null);
		}

		if (handleClickSelect) {
			handleClickSelect(Number(value));
		}
		locationEventsService.setSubject({
			type: 'LOCATION_CHANGED',
			payload: Number(value),
		});

		if (isDiagramBlockRoute) {
			navigate('/');
		}
	};

	return (
		<>
			{locations.length > 0 ? (
				<div className="location-combobox-wrapper" style={{ width: 220, ...aditionalStyle }}>
					<Combobox
						options={dataPicker}
						value={dataSelect}
						onChange={(val) => handleChange(val)}
						placeholder="Search a Location"
						searchable
						foundGroupLabel="FOUND"
						leftIcon={renderNetworkIcon((selectedLocationInfo as any)?.networkType)}
          size="small"
						style={{ width: '100%' }}
					/>
				</div>
			) : (
				''
			)}
		</>
	);
}
